import React, { useState } from "react";
import { Table } from "react-bootstrap";
import XLoader from "../loader/XLoader";
import PaginationNav from "../pagination/pagination";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";

interface Props {
  title: string;
  type: string;
  loader: boolean;
  data: any;
}

const CONTACTS_PER_PAGE = 5;

const SingleAnalyticsUI: React.FC<Props> = ({ title, type, loader, data }) => {
  const [currentPage, setCurrentPage] = useState(1);

  const totalContacts = data?.contacts?.length || 0;
  const totalPages = Math.ceil(totalContacts / CONTACTS_PER_PAGE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const paginatedContacts = (data?.contacts || []).slice(
    (currentPage - 1) * CONTACTS_PER_PAGE,
    currentPage * CONTACTS_PER_PAGE
  );

  return (
    <div className="table-listing single-analytics">
      {loader && <XLoader />}

      {/* All Analytic Details */}
      {!loader && data?.analytics?.length > 0 && (
        <div className="mb-4 p-3 py-3 border rounded bg-light">
          {data.analytics.map((v: any, i: number) => (
            <div key={i} className="row">

              {/* Left Column */}
              <div className="col-md-6">
                <div className="d-flex mb-2">
                  <div className="fw-bold me-2" style={{ width: "140px" }}>
                    {title}
                  </div>
                  <div>{v.title || v.name}</div>
                </div>

                <div className="d-flex mb-2">
                  <div className="fw-bold me-2" style={{ width: "140px" }}>
                    {type}:
                  </div>
                  <div>{v.type}</div>
                </div>

                <div className="d-flex mb-2">
                  <div className="fw-bold me-2" style={{ width: "140px" }}>
                    Recipients:
                  </div>
                  <div>{v.contact_count}</div>
                </div>

                <div className="d-flex mb-2">
                  <div className="fw-bold me-2" style={{ width: "140px" }}>
                    Hard Bounce:
                  </div>
                  <div>{v.hard_bounce + "%"}</div>
                </div>
              </div>

              {/* Right Column */}
              <div className="col-md-6">
                <div className="d-flex mb-2">
                  <div className="fw-bold me-2" style={{ width: "140px" }}>
                    Open Rate:
                  </div>

                  <div>
                    {(type === "Campaign Type"
                      ? ((v.open / v.contact_count) * 100).toFixed(2)
                      : v.open_rate) + "%"}
                  </div>
                </div>

                <div className="d-flex mb-2">
                  <div className="fw-bold me-2" style={{ width: "140px" }}>
                    Click Rate:
                  </div>
                  <div>
                    {(type === "Campaign Type"
                      ? ((v.click / v.contact_count) * 100).toFixed(2)
                      : v.click_rate) + "%"}
                  </div>
                </div>

                <div className="d-flex mb-2">
                  <div className="fw-bold me-2" style={{ width: "140px" }}>
                    Soft Bounce:
                  </div>
                  <div>{v.soft_bounce + "%"}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {!loader && (!data?.analytics || data?.analytics?.length === 0) && (
        <div className="mb-4 p-3 py-3 border rounded bg-light">
          No analytics found.
        </div>
      )}
      {/* Contacts Analytics */}
      <div className="rounded">
        <Table responsive>
          <thead>
            <tr>
              <th>Email</th>
              <th className="text-center">Delivered</th>
              <th className="text-center">Open</th>
              <th className="text-center">Click</th>
              <th className="text-center">Hard Bounce</th>
              <th className="text-center">Soft Bounce</th>
              <th className="text-center">Spam</th>
              <th className="text-center">Unsubscribe</th>
            </tr>
          </thead>
          <tbody>
            {paginatedContacts?.map((v: any) => {
              // A journey can send a contact multiple emails (one per step), each with its own log row.
              // Merge them so a checkmark reflects "happened on at least one step" instead of only the first email sent.
              const logs = v?.campaign_logs?.length ? v.campaign_logs : v?.journey_logs || [];
              const log = logs.reduce((merged: any, current: any) => {
                ["delivered", "open", "click", "hard_bounce", "soft_bounce", "spam", "unsubscribe"].forEach((key) => {
                  if (current?.[key]) merged[key] = 1;
                });
                return merged;
              }, {});
              return (
                <tr key={v.id}>
                  <td>{v.email}</td>
                  <td className={`text-center ${log?.delivered ? "text-success" : "text-danger"}`}>{log?.delivered ? <FontAwesomeIcon icon={faCheck} className="me-2" /> : "-"}</td>
                  <td className={`text-center ${log?.open ? "text-success" : "text-danger"}`}>{log?.open ? <FontAwesomeIcon icon={faCheck} className="me-2" /> : "-"}</td>
                  <td className={`text-center ${log?.click ? "text-success" : "text-danger"}`}>{log?.click ? <FontAwesomeIcon icon={faCheck} className="me-2" /> : "-"}</td>
                  <td className={`text-center ${log?.hard_bounce ? "text-success" : "text-danger"}`}>{log?.hard_bounce ? <FontAwesomeIcon icon={faCheck} className="me-2" /> : "-"}</td>
                  <td className={`text-center ${log?.soft_bounce ? "text-success" : "text-danger"}`}>{log?.soft_bounce ? <FontAwesomeIcon icon={faCheck} className="me-2" /> : "-"}</td>
                  <td className={`text-center ${log?.spam ? "text-success" : "text-danger"}`}>{log?.spam ? <FontAwesomeIcon icon={faCheck} className="me-2" /> : "-"}</td>
                  <td className={`text-center ${log?.unsubscribe ? "text-success" : "text-danger"}`}>{log?.unsubscribe ? <FontAwesomeIcon icon={faCheck} className="me-2" /> : "-"}</td>
                </tr>
              );
            })}
            {!loader && (!paginatedContacts || paginatedContacts.length === 0) && (
              <tr>
                <td colSpan={9}>
                  <p className="text-center no-records">No contact found.</p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {paginatedContacts?.length > 0 && (
        <PaginationNav
          totalPages={totalPages}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default SingleAnalyticsUI;
